import React, { useEffect, useMemo, useState, useCallback } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import Loading from "../components/Loading";
import MilestoneForm from "../components/MilestoneForm";
import FavHeart from "../components/FavHeart";
import ShareButton from "../components/ShareButton";
import CommentButton from "../components/CommentButton";
import { ExternalLink, Pencil, Trash2, Plus, Star, Sparkles, Rocket } from "lucide-react";
import { toast } from "sonner";

const MONTHS = [
  { n: 1, short: "JAN", full: "JANUARY" },
  { n: 2, short: "FEB", full: "FEBRUARY" },
  { n: 3, short: "MAR", full: "MARCH" },
  { n: 4, short: "APR", full: "APRIL" },
  { n: 5, short: "MAY", full: "MAY" },
  { n: 6, short: "JUN", full: "JUNE" },
  { n: 7, short: "JUL", full: "JULY" },
  { n: 8, short: "AUG", full: "AUGUST" },
  { n: 9, short: "SEP", full: "SEPTEMBER" },
  { n: 10, short: "OCT", full: "OCTOBER" },
  { n: 11, short: "NOV", full: "NOVEMBER" },
  { n: 12, short: "DEC", full: "DECEMBER" },
];

function CategoryChip({ value, type }) {
  const isAstro = type === "astronomy";
  return (
    <span
      className={`sat-label border px-2 py-0.5 text-[9px] inline-flex items-center gap-1 ${
        isAstro
          ? "border-fuchsia-400/60 text-fuchsia-300"
          : "border-[color:var(--sat-cyan)]/50 sat-cyan"
      }`}
      data-testid="milestone-category">
      {isAstro ? <Sparkles size={9} /> : <Rocket size={9} />}
      {value}
    </span>
  );
}

function MilestoneCard({ m, isAdmin, onEdit, onDelete }) {
  const [imgOk, setImgOk] = useState(!!m.image_url);
  const isAstro = m.type === "astronomy";
  return (
    <article
      className={`sat-brackets sat-surface border p-0 flex flex-col md:flex-row gap-0 transition-colors ${
        isAstro
          ? "border-fuchsia-400/30 hover:border-fuchsia-400"
          : "border-[color:var(--sat-border-bright)] hover:border-[color:var(--sat-cyan)]"
      }`}
      data-testid="milestone-card"
      data-milestone-type={m.type || "space"}
    >
      {m.image_url && imgOk && (
        <div className="md:w-56 md:min-w-[14rem] h-40 md:h-auto bg-[color:var(--sat-surface-2)] overflow-hidden border-b md:border-b-0 md:border-r border-[color:var(--sat-border)]">
          <img src={m.image_url} alt={m.title}
               onError={() => setImgOk(false)}
               className="w-full h-full object-cover" />
        </div>
      )}
      <div className="flex-1 p-4 md:p-5">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-2 flex-wrap">
            <CategoryChip value={m.category} type={m.type} />
            <span className="font-mono text-[11px] text-[color:var(--sat-text-dim)]" data-testid="milestone-date">
              {m.display_date}
            </span>
          </div>
          <div className="flex items-center gap-1">
            <CommentButton
              targetKind="milestone"
              targetId={m.id}
              title={m.title}
              size={12}
              testid={`comment-milestone-${m.id}`}
            />
            <ShareButton
              url={typeof window !== "undefined" ? `${window.location.origin}/history?m=${m.month}` : ""}
              title={m.title}
              size={12}
              testid={`share-milestone-${m.id}`}
              className="!p-1.5"
            />
            <FavHeart
              kind="milestone"
              ref_id={m.id}
              title={m.title}
              image={m.image_url || null}
              payload={{ month: m.month, category: m.category, type: m.type, display_date: m.display_date }}
              size={13}
              testid={`fav-milestone-${m.id}`}
            />
            {isAdmin && (
              <>
                <button
                  onClick={() => onEdit(m)}
                  className="p-1.5 border border-[color:var(--sat-border-bright)] text-[color:var(--sat-text-dim)] hover:text-[color:var(--sat-cyan)] hover:border-[color:var(--sat-cyan)] transition-colors"
                  data-testid="milestone-edit-btn"
                  aria-label="Edit milestone"
                ><Pencil size={12} /></button>
                <button
                  onClick={() => onDelete(m)}
                  className="p-1.5 border border-[color:var(--sat-border-bright)] text-[color:var(--sat-text-dim)] hover:text-[color:var(--sat-live)] hover:border-[color:var(--sat-live)] transition-colors"
                  data-testid="milestone-delete-btn"
                  aria-label="Delete milestone"
                ><Trash2 size={12} /></button>
              </>
            )}
          </div>
        </div>
        <h3 className="font-display font-bold text-lg md:text-xl mt-2 text-[color:var(--sat-text)]"
            data-testid="milestone-title">
          {m.title}
        </h3>
        <p className="text-sm text-[color:var(--sat-text-dim)] mt-2 leading-relaxed" data-testid="milestone-description">
          {m.description}
        </p>
        {m.source_url && (
          <a href={m.source_url} target="_blank" rel="noopener noreferrer"
             className={`inline-flex items-center gap-1.5 mt-3 sat-label hover:underline ${isAstro ? "text-fuchsia-300" : "sat-cyan"}`}
             data-testid="milestone-source-link">
            <ExternalLink size={11} /> SOURCE
          </a>
        )}
      </div>
    </article>
  );
}

export default function HistoryPage() {
  const [params, setParams] = useSearchParams();
  const currentMonth = new Date().getUTCMonth() + 1;
  const initial = parseInt(params.get("m"), 10);
  const [month, setMonth] = useState(
    initial >= 1 && initial <= 12 ? initial : currentMonth
  );
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const { user } = useAuth();
  const isAdmin = !!user?.is_admin;

  const load = useCallback(() => {
    setLoading(true);
    api.get("/milestones", { params: { month } })
      .then(r => setItems(r.data.results || []))
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  }, [month]);

  useEffect(() => { load(); }, [load]);

  const setMonthAndSync = (n) => {
    setMonth(n);
    setParams({ m: String(n) }, { replace: true });
  };

  const activeMonth = useMemo(() => MONTHS.find(m => m.n === month), [month]);

  const onDelete = async (m) => {
    if (!window.confirm(`Delete "${m.title}"?`)) return;
    try {
      await api.delete(`/milestones/${m.id}`);
      toast.success("Milestone deleted");
      load();
    } catch (e) {
      toast.error("Delete failed: " + (e.response?.data?.detail || e.message));
    }
  };

  const openCreate = () => { setEditing(null); setFormOpen(true); };
  const openEdit = (m) => { setEditing(m); setFormOpen(true); };
  const onSaved = () => { setFormOpen(false); setEditing(null); load(); };

  return (
    <div data-testid="page-history">
      <div className="mb-6 border-b border-[color:var(--sat-border)] pb-4 flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="sat-label sat-cyan">CHANNEL // 002.5</div>
          <h1 className="font-display font-bold text-3xl md:text-4xl mt-1">HISTORY</h1>
          <p className="text-sm text-[color:var(--sat-text-dim)] mt-2 max-w-2xl">
            Groundbreaking milestones in space and cosmology, keyed to the month they occurred. From the Big Bang to Falcon 9 landings.
          </p>
        </div>
        {isAdmin && (
          <button onClick={openCreate} className="sat-btn sat-btn-solid" data-testid="milestone-new-btn">
            <Plus size={13} /> NEW MILESTONE
          </button>
        )}
      </div>

      {/* Browse all shortcut */}
      <div className="mb-4">
        <Link to="/history/all"
          data-testid="history-browse-all"
          className="sat-btn !py-2 !px-3 inline-flex items-center gap-2">
          BROWSE ALL 120 MILESTONES →
        </Link>
      </div>

      {/* Month carousel */}
      <div
        className="mb-8 -mx-2 px-2 overflow-x-auto"
        data-testid="month-carousel"
      >
        <div className="flex gap-2 min-w-max pb-2">
          {MONTHS.map(mo => {
            const active = mo.n === month;
            return (
              <button
                key={mo.n}
                onClick={() => setMonthAndSync(mo.n)}
                data-testid={`month-btn-${mo.short.toLowerCase()}`}
                aria-pressed={active}
                className={`px-4 py-3 border font-display font-bold text-sm tracking-widest transition-all min-w-[70px] ${
                  active
                    ? "border-[color:var(--sat-cyan)] text-[color:var(--sat-cyan)] bg-[color:var(--sat-cyan-soft)] sat-box-glow"
                    : "border-[color:var(--sat-border-bright)] text-[color:var(--sat-text-dim)] hover:text-[color:var(--sat-cyan)] hover:border-[color:var(--sat-cyan)]"
                }`}
              >
                {mo.short}
              </button>
            );
          })}
        </div>
      </div>

      <div className="mb-4 flex items-baseline justify-between">
        <h2 className="font-display font-bold text-xl tracking-wide">
          <span className="text-[color:var(--sat-cyan)]">{activeMonth.full}</span>
          <span className="text-[color:var(--sat-text-dim)] text-sm ml-3" data-testid="milestone-count">
            {items.length} MILESTONE{items.length === 1 ? "" : "S"}
          </span>
        </h2>
      </div>

      {loading ? (
        <Loading message="RETRIEVING ARCHIVAL RECORDS" />
      ) : items.length === 0 ? (
        <div className="sat-surface border border-[color:var(--sat-border)] p-8 text-center" data-testid="history-empty">
          <div className="sat-label sat-cyan mb-2">// NO DATA</div>
          <p className="text-sm text-[color:var(--sat-text-dim)]">
            No milestones recorded for {activeMonth.full} yet.
          </p>
        </div>
      ) : (
        <div className="flex flex-col gap-4" data-testid="milestone-list">
          {items.map(m => (
            <MilestoneCard key={m.id} m={m} isAdmin={isAdmin} onEdit={openEdit} onDelete={onDelete} />
          ))}
        </div>
      )}

      {formOpen && (
        <MilestoneForm
          initial={editing}
          defaultMonth={month}
          onClose={() => { setFormOpen(false); setEditing(null); }}
          onSaved={onSaved}
        />
      )}
    </div>
  );
}
